import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getStories, Story, toggleStoryPin, notifyStory } from '../api/stories'
import { Calendar, User, ArrowRight, BookOpen, TrendingUp, Star, Pin, Bell, Crown } from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import { resolveImageUrl } from '../utils/imageUrl'

const BlogPage = () => {
  const { user } = useUser()
  const [stories, setStories] = useState<Story[]>([])
  const [loading, setLoading] = useState(true)
  const [adminLoading, setAdminLoading] = useState<string | null>(null)

  const toApiImageUrlIfPossible = (src: string) => resolveImageUrl(src)

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const data = await getStories()
        // Show all stories, not just pinned ones
        setStories(data)
      } catch (error) {
        console.error('Error fetching stories:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStories()
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getTimeAgo = (dateString: string) => {
    const now = new Date()
    const storyDate = new Date(dateString)
    const diffInHours = Math.floor((now.getTime() - storyDate.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 24) {
      return `${diffInHours} hours ago`
    } else {
      const diffInDays = Math.floor(diffInHours / 24)
      return `${diffInDays} days ago`
    }
  }

  const isAdmin = user?.userType === 'ADMIN'
  const pinnedStories = stories.filter(story => story.isPinned)
  const featuredStories = stories.filter(story => !story.isPinned).slice(0, 3)
  const recentStories = stories.filter(story => !story.isPinned).slice(0, 6)

  const handlePinStory = async (storyId: string) => {
    if (!isAdmin) return
    
    setAdminLoading(storyId)
    try {
      await toggleStoryPin(storyId)
      // Refresh stories
      const updatedStories = await getStories()
      setStories(updatedStories)
    } catch (error) {
      console.error('Error pinning story:', error)
      alert('Failed to pin story')
    } finally {
      setAdminLoading(null)
    }
  }

  const handleNotifyStory = async (storyId: string) => {
    if (!isAdmin) return
    
    setAdminLoading(storyId)
    try {
      await notifyStory(storyId)
      alert('Notification sent successfully!')
    } catch (error) {
      console.error('Error sending notification:', error)
      alert('Failed to send notification')
    } finally {
      setAdminLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading stories...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/40">
      {/* Hero Section */}
      <section className="bg-white/80 backdrop-blur-sm border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/20 mb-6">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-extrabold text-neutral-900 mb-3 tracking-tight">Stories</h1>
            <div className="w-16 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mx-auto mb-6"></div>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Stay updated with the latest stories, success cases, and community impact from HarvestHelp
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Pinned Stories */}
        {pinnedStories.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
              <Pin className="h-6 w-6 text-yellow-500 mr-2" />
              Pinned Stories
              {isAdmin && (
                <span className="ml-2 text-sm text-gray-500 flex items-center">
                  <Crown className="h-4 w-4 mr-1" />
                  Admin
                </span>
              )}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pinnedStories.map((story) => (
                <div key={story.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border-2 border-yellow-200">
                  {story.imageUrl && (
                    <img 
                      src={toApiImageUrlIfPossible(story.imageUrl)} 
                      alt={story.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        const original = story.imageUrl || ''
                        const apiUrl = toApiImageUrlIfPossible(original)
                        if (apiUrl && e.currentTarget.src !== apiUrl) {
                          e.currentTarget.src = apiUrl
                          return
                        }
                        e.currentTarget.src =
                          'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                      }}
                    />
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(story.createdAt)}
                        <span className="mx-2">•</span>
                        <User className="h-4 w-4 mr-1" />
                        {story.authorName}
                      </div>
                      <Pin className="h-4 w-4 text-yellow-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                      {story.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {story.excerpt || story.content.substring(0, 150) + '...'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {story.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handlePinStory(story.id)}
                              disabled={adminLoading === story.id}
                              className="text-gray-500 hover:text-red-500 p-1 rounded"
                              title="Unpin story"
                            >
                              <Pin className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleNotifyStory(story.id)}
                              disabled={adminLoading === story.id}
                              className="text-gray-500 hover:text-blue-500 p-1 rounded"
                              title="Send notification"
                            >
                              <Bell className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <Link 
                          to={`/stories/${story.id}`} 
                          className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
                        >
                          Read More
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Featured Stories */}
        {featuredStories.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
              <Star className="h-6 w-6 text-yellow-500 mr-2" />
              Featured Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredStories.map((story) => (
                <div key={story.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  {story.imageUrl && (
                    <img 
                      src={toApiImageUrlIfPossible(story.imageUrl)} 
                      alt={story.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        const original = story.imageUrl || ''
                        const apiUrl = toApiImageUrlIfPossible(original)
                        if (apiUrl && e.currentTarget.src !== apiUrl) {
                          e.currentTarget.src = apiUrl
                          return
                        }
                        e.currentTarget.src =
                          'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                      }}
                    />
                  )}
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(story.createdAt)}
                      <span className="mx-2">•</span>
                      <User className="h-4 w-4 mr-1" />
                      {story.authorName}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                      {story.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {story.excerpt || story.content.substring(0, 150) + '...'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {story.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <>
                          <button
                            onClick={() => handlePinStory(story.id)}
                            disabled={adminLoading === story.id}
                            className="text-gray-500 hover:text-yellow-500 p-1 rounded"
                            title="Pin story"
                          >
                            <Pin className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleNotifyStory(story.id)}
                            disabled={adminLoading === story.id}
                            className="text-gray-500 hover:text-blue-500 p-1 rounded"
                            title="Send notification"
                          >
                            <Bell className="h-4 w-4" />
                          </button>
                          </>
                        )}
                        <Link 
                          to={`/stories/${story.id}`} 
                          className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
                        >
                          Read More
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recent Stories */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
            <TrendingUp className="h-6 w-6 text-primary-600 mr-2" />
            Recent Posts
          </h2>
          
          {recentStories.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Stories Found</h3>
              <p className="text-gray-600 mb-6">
                Be the first to share your story with the community!
              </p>
              <Link
                to="/stories/create"
                className="btn-primary inline-flex items-center"
              >
                Write Your Story
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentStories.map((story) => (
                <div key={story.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  {story.imageUrl && (
                    <img 
                      src={toApiImageUrlIfPossible(story.imageUrl)} 
                      alt={story.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        const original = story.imageUrl || ''
                        const apiUrl = toApiImageUrlIfPossible(original)
                        if (apiUrl && e.currentTarget.src !== apiUrl) {
                          e.currentTarget.src = apiUrl
                          return
                        }
                        e.currentTarget.src =
                          'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                      }}
                    />
                  )}
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <Calendar className="h-4 w-4 mr-1" />
                      {getTimeAgo(story.createdAt)}
                      <span className="mx-2">•</span>
                      <User className="h-4 w-4 mr-1" />
                      {story.authorName}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
                      {story.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {story.excerpt || story.content.substring(0, 120) + '...'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {story.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handlePinStory(story.id)}
                              disabled={adminLoading === story.id}
                              className="text-gray-500 hover:text-yellow-500 p-1 rounded"
                              title="Pin story"
                            >
                              <Pin className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleNotifyStory(story.id)}
                              disabled={adminLoading === story.id}
                              className="text-gray-500 hover:text-blue-500 p-1 rounded"
                              title="Send notification"
                            >
                              <Bell className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <Link 
                          to={`/stories/${story.id}`} 
                          className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
                        >
                          Read More
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Share Your Story</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Have an inspiring story about volunteering, donating, or making a difference in your community? 
              Share it with the HarvestHelp community!
            </p>
            <Link
              to="/stories/create"
              className="btn-primary inline-flex items-center text-lg px-8 py-3"
            >
              <BookOpen className="mr-2 h-5 w-5" />
              Write Your Story
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BlogPage 